import { DependencyList } from 'react';
import shallowEqual, { isDefined } from './array.helpers';
import {
  OptionallyRequired,
  OptionallyRequiredOptions,
  TypeMap,
} from '../models/type-helpers.model';
import { isType } from './object.helpers';
import { getProcessEnvironment } from './environment.helpers';
import { logTotalTime } from './debug.helpers';
import { compare } from './compare.helpers';

/**
 * True when this process is applying database migrations (`db-migrate`).
 * Prefer {@link ServerEnvironment.NODON_MIGRATION} — `npm_lifecycle_script`
 * is only set when the binary is invoked as an npm script, not via `npx`.
 */
export const inMigrationMode = (): boolean => {
  const env = getProcessEnvironment();
  if (!env) return false;
  if (env.NODON_MIGRATION === 'true') return true;
  return env.npm_lifecycle_script === 'db-migrate';
};

export type AnyFn = (...args: any[]) => any;
export type VoidFn = () => void;

/**
 * Get trigger function that calls provided function with provided arguments
 * Good to use for event handlers like onClick etc
 */
export const getTrigger = <F extends AnyFn, P extends Parameters<F>>(
  fn: F,
  ...args: P
): VoidFn => {
  if (typeof fn !== 'function') {
    throw new TypeError('Expected a function');
  }
  return () => {
    fn(...args);
  };
};

type CacheByArgumentsOptions = {
  /**
   * Unique key for this cache. If not provided, a unique key is generated per invocation
   * so a single root-scope wrap works without collision. Pass explicitly when multiple
   * cached functions exist so caches do not collide (e.g. after minification String(fn) can be identical).
   */
  key?: string;

  /**
   * Comparator to use to compare arguments. If not provided, compare.equal.shallow will be used.
   */
  comparator?: (a: unknown, b: unknown) => boolean;

  disabled?: boolean;
};

/**
 * Cache of results by arguments.
 */
const cacheByArgumentsCache = new Map<
  string,
  { result: unknown; args: unknown[] }
>();

let cacheByArgumentsId = 0;

/**
 * Wraps a function and returns one with the same signature that caches results by arguments.
 * When {@link CacheByArgumentsOptions.key} is omitted, a unique key is used for that invocation (safe for single root-scope use).
 *
 * @param fn - Function to wrap.
 * @param options - Options for the cache (key optional when wrapping once at root scope but is needed when caching inside a function).
 * @returns Cached function with the same arguments and return type.
 */
export function cacheByArguments<TArgs extends unknown[], R>(
  fn: (...args: TArgs) => R,
  {
    key = `cacheByArguments:${++cacheByArgumentsId}`,
    comparator = compare.equal.shallow,
    disabled,
  }: CacheByArgumentsOptions = {},
): (...args: TArgs) => R {
  return (...args: TArgs): R => {
    const entry = cacheByArgumentsCache.get(key);
    const isDisabled = disabled || inMigrationMode();

    // If we had a value in the cache and the arguments are the same, return the cached value
    if (!isDisabled && entry !== undefined && comparator(entry.args, args)) {
      return entry.result as R;
    }

    // Run the function and cache the result
    const result = fn(...args);
    cacheByArgumentsCache.set(key, { result, args: args as unknown[] });
    return result as R;
  };
}

type CacheFactoryOptions = {
  /**
   * Whether to log the time it takes to execute the factory function and print how many times cache was hit or missed.
   * Set to string to track time by a specific label.
   */
  debug?: boolean | string;
  /**
   * Set to true to disable the cache
   */
  disabled?: boolean;
};

let cacheFactoryValueRecord: Record<string, unknown> = {};

/**
 * Memoize/cache the result of a factory function. Will only recompute the memoized value when one of the deps has changed (on that specific key).
 * @param factory Factory function to cache. Will only be called initially and if the deps
 * @param key Key used to invalidate the cache. Deps and value are stored used by this key. And if the key changes, the memoized value will be recomputed.
 * @param deps Dependencies to invalidate the cache by
 * @param options Options for the cache
 */
export function cacheFactory<T>(
  factory: () => T,
  key: string,
  deps: DependencyList | undefined = [],
  { debug = false, disabled = false }: CacheFactoryOptions = {},
): T {
  const debugLabel = typeof debug === 'string' ? debug : `cacheFactory[${key}]`;
  const shouldDebug = Boolean(debug);

  // Don't cache in migration mode or if disabled
  if (inMigrationMode() || disabled) {
    return logTotalTime(factory, `${debugLabel}.disabled`, shouldDebug);
  }

  // Run factory function first time or if deps have changed
  if (hasChangedDependencies(key, deps, shouldDebug)) {
    try {
      // Execute factory while logging the time it takes to execute if debug is true
      cacheFactoryValueRecord[key] = logTotalTime(
        factory,
        `${debugLabel}.miss`,
        shouldDebug,
      );
    } catch (error) {
      delete changedDependencyRecord[key]; // Clear deps so it's recalculated next time
      throw error instanceof Error
        ? error
        : new Error('Cache factory fn error');
    }
  } else if (debug) {
    // eslint-disable-next-line no-console
    console.count(`${debugLabel}.hit`);
  }

  // It should only be undefined if factory returns undefined so cast to T
  return getCacheFactoryValue<T>(key) as T;
}

/**
 * Get cached value from factory
 * @param key Key to get value from
 * @param throwIfNotFound True to throw if value is not found, false to return undefined.
 * Only set this to true if the cacheFn is guaranteed to always return a value
 * @returns
 */
export const getCacheFactoryValue = <
  T,
  E extends OptionallyRequiredOptions = false,
>(
  key: string,
  throwIfNotFound: E = false as E,
): OptionallyRequired<T, E> =>
  required(cacheFactoryValueRecord[key], throwIfNotFound);

/**
 * Reset both cacheFactory and cacheByArguments caches and changed dependencies (they are reset together to avoid invalidating cache).
 * Does not reset cacheByArgumentsId so existing module-scope cacheByArguments keys remain unique.
 */
export function cacheFactoryReset(): void {
  cacheFactoryValueRecord = {};
  changedDependencyRecord = {};
  cacheByArgumentsCache.clear();
}
// Register global reset function for unit tests
if (typeof globalThis === 'object') {
  (globalThis as any).cacheFactoryReset = cacheFactoryReset;
}

let changedDependencyRecord: Record<string, DependencyList | undefined> = {};

/**
 * Last dependency list passed to {@link cacheFactory} for `key`.
 * Does not record a new list — unlike {@link hasChangedDependencies}.
 */
export const getCacheFactoryDependencies = (
  key: string,
): DependencyList | undefined => changedDependencyRecord[key];

export function hasChangedDependencies(
  key: string,
  deps: DependencyList | undefined = [],
  debug?: boolean,
): boolean {
  const prevDeps = changedDependencyRecord[key];
  changedDependencyRecord[key] = deps;
  const hasChanged = !shallowEqual(prevDeps, deps);

  if (hasChanged && debug) {
    for (let i = 0; i < deps.length; i++) {
      const dep = deps[i];
      const prevDep = prevDeps ? prevDeps[i] : undefined;
      if (dep !== prevDep) {
        console.info(
          `hasChangedDependencies[${key}], changed dependency (index ${i}):`,
          prevDep,
          'to:',
          dep,
        );
      }
    }
  }
  return hasChanged;
}

/**
 * Optionally require value to be defined and crash if it's not defined
 * @param value Value to check
 * @param requireOrErrorMsg False to not require value, true to throw or string to throw with custom message
 * @returns
 */
export const required = <
  T,
  B extends OptionallyRequiredOptions = true,
  R = OptionallyRequired<T, B>,
>(
  value: T,
  requireOrErrorMsg: B = true as B,
): R => {
  if (requireOrErrorMsg && !isDefined(value)) {
    throw new Error(
      typeof requireOrErrorMsg === 'string'
        ? requireOrErrorMsg
        : 'Value is required',
    );
  }
  return value as any as R;
};

export const requiredType = <T, K extends keyof TypeMap>(
  value: T,
  ...types: K[]
): TypeMap<T>[K] => {
  if (!isType(value, ...types)) {
    throw new Error(`Value is not of type ${types.join(' or ')}`);
  }
  return value;
};

export const assertUnreachable = (nothing: never) => {
  throw new Error(`Unexpected value: ${String(nothing)}`);
};

/**
 * Lazy generator function that caches the result of the generator function first time it is called.
 * This will only run the generator function once and cache the result, subsequent calls will return the cached result.
 *
 * @param generator Function to generate the value
 * @returns Function that returns the cached value
 */
export const lazyCacheGenerate = <ReturnType>(generator: () => ReturnType) => {
  const NO_CACHE_SYMBOL = Symbol('NO_CACHE');
  let cache: ReturnType | typeof NO_CACHE_SYMBOL = NO_CACHE_SYMBOL;
  return (): ReturnType => {
    if (cache === NO_CACHE_SYMBOL) cache = generator();
    return cache;
  };
};
